import React, { useCallback, useRef } from 'react';
import {
	Badge,
	Box,
	Button,
	Flex,
	Popover,
	PopoverBody,
	PopoverContent,
	PopoverTrigger,
	Portal,
	Text,
	useDisclosure,
} from '@chakra-ui/react';
import { useFormik } from 'formik';

import { GetBookDocument, GetBookQuery } from 'features/book/queries.gen';
import { ChapterByIdDocument } from 'features/story-editor/header/queries.gen';
import {
	BookProperty,
	ButtonType,
	trackPropertyChanged,
} from 'shared/analytics';
import { useAppToast } from 'shared/hooks/toast';
import { PencilIcon } from 'shared/icons';
import { ArrayElement } from 'shared/model';
import { UpdateTagsForm } from 'shared/ui/update-tags-form';

import { useUpdateChapterMutation } from '../../queries.gen';

export interface Props {
	chapter: ArrayElement<GetBookQuery['book']['chapters']>;
}

export const UpdateVideoForm: React.FC<Props> = ({ chapter }) => {
	const toast = useAppToast();
	const { onOpen, onClose, isOpen } = useDisclosure();
	const containerRef = useRef<HTMLDivElement | null>(null);

	const [updateChapter] = useUpdateChapterMutation({
		onCompleted: () => {
			onClose();
			toast({
				title: 'Chapter video updated successfully',
				status: 'success',
			});
		},
		onError: () =>
			toast({
				title: 'Chapter video was not updated',
				status: 'error',
			}),
		refetchQueries: [ChapterByIdDocument, GetBookDocument],
	});

	const onSubmitStatusModal = useCallback(
		async (formValue: any) => {
			await updateChapter({
				variables: {
					chapter: {
						id: chapter?.id || '',
						isVideo: formValue[0] === 'Yes',
					},
				},
			});
			trackPropertyChanged({
				entity: 'chapter',
				property: BookProperty.IS_VIDEO,
				value: formValue[0],
				id: chapter?.id,
			});
		},
		[chapter?.id, updateChapter],
	);

	const updateStatusForm = useFormik({
		initialValues: {
			isVideo: chapter?.isVideo ? ['Yes'] : ['No'],
		},
		validateOnChange: true,
		enableReinitialize: true,
		onSubmit: (values) => onSubmitStatusModal({ ...values }),
	});

	const onCloseStatusModal = useCallback(() => {
		onClose();
		updateStatusForm.resetForm();
	}, [onClose, updateStatusForm]);

	if (!chapter) return null;

	return (
		<Box
			pb="15px"
			borderTop="1px solid #eeeef2"
			borderBottom="1px solid #eeeef2"
			ref={containerRef}
		>
			<Flex
				position="relative"
				p="13px 0"
				justifyContent="space-between"
				alignItems="center"
			>
				<Text fontWeight={500} fontSize="14px" lineHeight="114%">
					Is video
				</Text>
				<Popover
					isOpen={isOpen}
					onOpen={onOpen}
					onClose={onCloseStatusModal}
					placement="bottom-end"
					offset={[10, 10]}
				>
					<PopoverTrigger>
						<Button size="circle" variant="secondary">
							<PencilIcon w={6} h={6} />
						</Button>
					</PopoverTrigger>
					<Portal containerRef={containerRef}>
						<PopoverContent>
							<PopoverBody>
								<UpdateTagsForm
									submitButtonName={ButtonType.EDIT_CHAPTER_VIDEO}
									isMulti={false}
									formTitle="Is Video"
									onClose={onCloseStatusModal}
									options={['Yes', 'No']}
									selectedOptions={updateStatusForm.values.isVideo}
									onChange={(selected) =>
										updateStatusForm.setFieldValue('isVideo', selected, true)
									}
									onSubmit={() =>
										onSubmitStatusModal(updateStatusForm.values.isVideo)
									}
								/>
							</PopoverBody>
						</PopoverContent>
					</Portal>
				</Popover>
			</Flex>
			<Flex>
				<Badge>{chapter.isVideo ? 'Yes' : 'No'}</Badge>
			</Flex>
		</Box>
	);
};
